/**
 * useClientExtractionStatus — TASK-817.
 *
 * Accepting a document starts a read of it that outlives the accept response by tens of
 * seconds. Nothing in the UI used to say so: the KYC workspace counted the fields that had
 * arrived (none, yet) and rendered "Collecting · N fields still missing. Next: request the
 * missing evidence from the client" — advice to chase a client for a document the platform
 * was holding and actively reading.
 *
 * This polls the server's derived view of that work. Two cadences, because the question
 * changes: while something is unsettled the answer moves and is worth asking about often;
 * while nothing is, the only thing that could change is a document accepted from somewhere
 * else in the app, which a slow poll catches without burning a request every few seconds.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import documentService, {
  type ClientExtractionStatus,
} from '../services/documentService';
import { logger } from '../utils/logger';

const ACTIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 30000;

export interface UseClientExtractionStatus {
  status: ClientExtractionStatus | null;
  /** True only for work genuinely in flight — a stalled document is not "still going". */
  isExtracting: boolean;
  /** Failed plus stalled: the documents that need a person. */
  needsAttention: number;
  retry: (documentId: string) => Promise<void>;
  refresh: () => void;
}

export function useClientExtractionStatus(
  clientId: string | undefined,
  /** Fired when an extraction finishes, so the caller can re-read what it produced. */
  onExtractionSettled?: () => void,
): UseClientExtractionStatus {
  const [status, setStatus] = useState<ClientExtractionStatus | null>(null);
  const [nonce, setNonce] = useState(0);

  // Held in refs so a change does not restart the poll loop mid-flight.
  const settledRef = useRef(onExtractionSettled);
  settledRef.current = onExtractionSettled;
  const previousRef = useRef<{ extracting: number; lastExtractedAt?: string | null } | null>(
    null
  );

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      let next: ClientExtractionStatus | null = null;
      try {
        next = await documentService.getClientExtractionStatus(clientId);
        if (cancelled) return;
        setStatus(next);

        // "Something finished" is the signal the caller actually needs — it means new
        // fields exist upstream. Detected from the in-flight count falling or from a newer
        // success timestamp, so an extraction that lands between two ticks still counts.
        const previous = previousRef.current;
        if (
          previous &&
          (next.extracting < previous.extracting ||
            (next.last_extracted_at ?? null) !== (previous.lastExtractedAt ?? null))
        ) {
          settledRef.current?.();
        }
        previousRef.current = {
          extracting: next.extracting,
          lastExtractedAt: next.last_extracted_at,
        };
      } catch (e) {
        if (cancelled) return;
        // A failed status read is not worth a toast: it says nothing about the documents,
        // and the next tick will retry. Keep the last known status on screen rather than
        // blanking it, which would read as "nothing is happening".
        logger.warn('[useClientExtractionStatus] status poll failed:', e);
      }

      const busy = next ? next.extracting > 0 : false;
      timer = setTimeout(tick, busy ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clientId, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const retry = useCallback(
    async (documentId: string) => {
      await documentService.retryExtraction(documentId);
      refresh();
    },
    [refresh]
  );

  return {
    status,
    isExtracting: (status?.extracting ?? 0) > 0,
    needsAttention: (status?.failed ?? 0) + (status?.stalled ?? 0),
    retry,
    refresh,
  };
}

export default useClientExtractionStatus;
